import IInOutHistory, {
  IInOutItem,
  TInOutHistory,
} from "@funds/components/InOutHistory/IInOutHistory";
import Pagination from "@appComponents/Pagination/Pagination";
import React, { useContext, useEffect, useState } from "react";
import { FundsAppContext } from "@features/context";
import FundsService from "@services/Funds.service";
import Loader from "@appComponents/Loader/Loader";
import InOutHistoryRow from "@funds/components/InOutHistory/components/InOutHistoryRow/InOutHistoryRow";
import { classNames } from "@features/utils/reactFunctions";
import { ETransactionType } from "@funds/enums/ETransactionType";
import { toUpper, renameMainCurrency } from "@features/utils/functions";
import NoItemsSVG from "./noItems.svg";

import * as style from "./InOutHistoryStyle.module.scss"
import * as elements from "@appStyles/_elements.module.scss";
import { Space } from "antd";

const InOutHistory: React.FunctionComponent<IInOutHistory> = React.memo(
  (props) => {
    const { currency, transactionType } = props;

    const { investorId } = useContext(FundsAppContext);
    const [historyState, setState] = useState<TInOutHistory>({
      transactions: [],
      paginationConfig: {
        currentPage: 0,
        totalCount: 0,
        perPage: 0,
      },
      isLoading: false,
    });

    useEffect(() => {
      fetchData();
    }, [props.currency, transactionType]);

    const getHistory = (
      investorId: number,
      currency?: string,
      page?: number
    ) => {
      switch (transactionType) {
        case ETransactionType.deposit:
          return FundsService.getDepositsHistory(investorId, currency, page);
        case ETransactionType.withdraw:
          return FundsService.getWithdrawsHistory(investorId, currency, page);
        case ETransactionType.purchase:
          return FundsService.getPurchasesHistory(currency, page);
        case ETransactionType.tradeOut:
        case ETransactionType.tradeIn:
          return FundsService.getTradingHistory(currency, page);
      }
      return null;
    };

    const fetchData = (page: number = 1) => {
      if (investorId) {
        setState((prevState: TInOutHistory) => {
          return { ...prevState, isLoading: true };
        });
        getHistory(investorId, props.currency, page)!.then((res) => {
          setState({
            transactions: res.data.items,
            isLoading: false,
            paginationConfig: {
              currentPage: res.data.page,
              perPage: res.data.per_page,
              totalCount: res.data.total_count,
            },
          });
        });
      }
    };

    const cancelWithdraw = (withdrawId: number) => {
      const changeWithdrawStatus = (
        withdraw: IInOutItem,
        withdraws: IInOutItem[]
      ) => {
        return withdraws.map((withdrawItem: IInOutItem) => {
          return withdrawItem.id === withdraw.id
            ? { ...withdrawItem, status: "cancelled" }
            : withdrawItem;
        });
      };

      if (investorId) {
        FundsService.cancelWithdraw(investorId, withdrawId).then((res) => {
          props.updateWalletOnCancel!({
            amount: res.data.amount,
            type: ETransactionType.cancel,
            currency: currency,
          });
          setState((prevState: TInOutHistory) => {
            return {
              ...prevState,
              transactions: changeWithdrawStatus(
                res.data,
                prevState.transactions
              ),
            };
          });
        });
      }
    };

    addInOutItem = (item: IInOutItem) => {
      if (historyState.paginationConfig.currentPage === 1) {
        let transactions: IInOutItem[] = [];
        if (historyState.transactions.length >= 10) {
          transactions = [item, ...historyState.transactions.slice(0, -1)];
        } else {
          transactions = [item, ...historyState.transactions];
        }

        setState((prevState) => {
          return { ...prevState, transactions };
        });
      }
    };

    const humanizedType = [
      ETransactionType.tradeIn,
      ETransactionType.tradeOut,
    ].includes(transactionType)
      ? "trading"
      : transactionType;

    const renderContent = () => {
      if (historyState.isLoading) {
        return <Loader moreSpace={true} />;
      }

      if (historyState.transactions.length > 0) {
        return (
          <>
            <div className={elements.zebraTableContainer}>
              <div className={elements.zebraTableRow}>
                {[ETransactionType.tradeIn, ETransactionType.tradeOut].includes(
                  transactionType
                )
                  ? ["Date", "In", "Out"].map((label: string) => {
                      return (
                        <div
                          key={label}
                          className={classNames(
                            elements.zebraTableItem,
                            label === "Date" && style.zebraTableItemFirst,
                            label === "Out" && style.zebraTableItemLast
                          )}
                        >
                          {label}
                        </div>
                      );
                    })
                  : ["Date", "Status", "Amount"].map((label: string) => {
                      return (
                        <div
                          key={label}
                          className={classNames(
                            elements.zebraTableItem,
                            label === "Amount" && style.zebraTableItemLast,
                            label === "Status" && style.zebraTableItemMiddle,
                            label === "Date" && style.zebraTableItemFirst
                          )}
                        >
                          {label}
                        </div>
                      );
                    })}
              </div>
              {historyState.transactions.map((inOutItem: IInOutItem) => {
                return (
                  <InOutHistoryRow
                    transactionType={transactionType}
                    handleCancel={cancelWithdraw}
                    inOutItem={inOutItem}
                    key={inOutItem.id}
                  />
                );
              })}
            </div>
            <Pagination
              currentPage={historyState.paginationConfig.currentPage}
              totalCount={historyState.paginationConfig.totalCount}
              maxPerPage={historyState.paginationConfig.perPage}
              filterData={fetchData}
            />
          </>
        );
      }

      return (
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center", margin: '30px auto 10px' }}
        >
          <NoItemsSVG />
          <h4 className={elements.noItems} style={{ textAlign: "center", color: '#A2A2A2' }}>
            No {humanizedType}
            {humanizedType !== "trading" ? "s" : ""} history for
            <br /> selected currency.
          </h4>
        </Space>
      );
    };

    return (
      <div>
        {currency && (
          <h2 data-cy="transactions-history-title" className={style.headline}>
            {toUpper(humanizedType)} History (
            {renameMainCurrency(currency).toUpperCase()})
          </h2>
        )}
        {renderContent()}
      </div>
    );
  }
);

export let addInOutItem: ((i: IInOutItem) => void) | undefined = undefined;
export default InOutHistory;
